import React, { useState, useEffect, useContext, useRef } from 'react';
import classNames from 'classnames';
import { useLocation, useHistory } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import allyMaintainDisabled from 'ally.js/maintain/disabled';
import DecodeGlobalContext from '../../redux/decodeGlobalContext';
import deviceInfo from '../../helpers/deviceInfo';
import { breakpoints } from '../../config/constants';
import Image from '../Image';
import Logo from '../Logo';
import useGlobalNavContent from '../../hooks/useGlobalNavContent';

import './styles.scss';
import { SET_PREVIOUS_ARTICLE_FOCUS } from '../../redux/actionTypes';

const NavPagination = ({ page, total }) => {
  const { dispatch } = useContext(DecodeGlobalContext);
  useEffect(
    () => () => {
      dispatch({
        type: 'SET_NAVIGATION_PAGINATION',
        payload: null
      });
    },
    []
  );

  return (
    <div className="navigation__card-pagination">
      <span>{page}</span>
      <span />
      <span>{total}</span>
    </div>
  );
};

const DecodeLogo = ({ url }) => {
  // in a case where logo is PNG
  const [oneFrameOnly, setOneFrameOnly] = useState(true);
  return (
    <Image
      log={true}
      classes="navigation__logo"
      image={url}
      sizes={[{ actual: 100, intrinsic: 200 }]}
      imgixParams={{ frame: oneFrameOnly ? 1 : -120 }}
      alt="Decode Logo"
      onLoad={() => oneFrameOnly && setOneFrameOnly(false)}
    />
  );
};

const Navigation = () => {
  const location = useLocation();
  const history = useHistory();
  const { state, dispatch } = useContext(DecodeGlobalContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNavBarItemsHidden, setIsNavBarItemsHidden] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredNav, setFilteredNav] = useState([]);
  const [redirectSlug, setRedirectSlug] = useState('');
  const [redirectToHome, setRedirectToHome] = useState(false);
  const [isNavZoomed, setIsNavZoomed] = useState(false);
  const [logoData, setLogoData] = useState(null);
  const [withDelay, setWithDelay] = useState(false);
  const navScrollRef = useRef();
  const lastScrollTop = useRef(0);
  const allyDisableRef = useRef(null);
  const navContents = useGlobalNavContent();
  const { navMenuBackground, navPagination, navMenuHidden } = state;
  const {
    logo = [],
    logoMobile = [],
    description = '',
    expandedCopy = '',
    navigationItems = []
  } = navContents || {};
  const isArticlePage = location.pathname.includes('/article');
  const isNavMenuBlack = navMenuBackground === 'black' && isArticlePage;
  const toggleButtonClasses = classNames('navigation__menu-toggle', {
    'navigation__menu-toggle--is-clicked': isMenuOpen,
    'navigation__menu-toggle--black': isNavMenuBlack
  });
  const navigationClasses = classNames('navigation', {
    'navigation--black': isNavMenuBlack,
    'navigation--white': !isNavMenuBlack,
    'navigation--menu-opened': isMenuOpen,
    'navigation--hidden': navMenuHidden,
    'navigation--zoomed': isNavZoomed
  });
  const logoFill = isNavMenuBlack ? 'white' : 'black';
  const searchText = location.search || '';
  const isMobile = window.innerWidth < breakpoints.tablet;
  const isMobileOrTablet = window.innerWidth < breakpoints.smallDesktop;
  let categoryText = '';

  // check if browser is safari
  const isBrowserSafari =
    deviceInfo.isSafari || deviceInfo.isIOS ? 'navigation__category-link--isSafari' : '';

  if (searchText.length) {
    const queryString = searchText.substr(1).split('=');

    categoryText = queryString.length > 1 && queryString[0] === 'category' ? queryString[1] : '';
  }

  function handleScrollEvent() {
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (navScrollRef.current) {
      window.cancelAnimationFrame(navScrollRef.current);
    }

    navScrollRef.current = window.requestAnimationFrame(() => {
      setIsNavBarItemsHidden(currentScrollTop > 10 && currentScrollTop > lastScrollTop.current);

      if (!isArticlePage) {
        lastScrollTop.current = currentScrollTop <= 0 ? 0 : currentScrollTop;
      }
    });
  }

  // set the correct logo for mobile, tablet and desktop
  useEffect(() => {
    const newLogo = isMobile === true ? logoMobile : logo;
    setLogoData(newLogo);
  }, [isMobile]);

  useEffect(() => {
    // Adde scroll handler for Navigation only for Homepage
    // and Moble/Tablet breakpoints.
    const shouldUseScroll = isMobile && !isArticlePage;

    if (shouldUseScroll) {
      window.addEventListener('scroll', handleScrollEvent, false);
    }

    // Implement the use of window.visualViewport to detect changes in zoom level.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);

      handleVisualViewportResize();
    }

    const API_PATH = window.env.RAZZLE_API_V2_PATH;
    const main = API_PATH.match(/^(?:\/\/|[^\/]+)*/);
    getCategories(main);

    return () => {
      if (shouldUseScroll) {
        window.removeEventListener('scroll', handleScrollEvent);
      }

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
      }
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';

    // Set main container to be hidden when Navigation is opened to
    // prevent tab and screen-readers to access contents.
    if (isMenuOpen) {
      allyDisableRef.current = allyMaintainDisabled({
        context: '.decode',
        filter: '.navigation'
      });
    } else if (allyDisableRef.current) {
      allyDisableRef.current.disengage();
      allyDisableRef.current = null;
    }

    document.querySelector('main').setAttribute('aria-hidden', isMenuOpen);

    if (isMobileOrTablet) {
      const timeout = setTimeout(() => {
        setWithDelay(isMenuOpen);
      }, 300);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isMenuOpen]);

  const getFilteredItems = navList => {
    const arr = [];

    navList.forEach(item => {
      const nav = navigationItems.find(navItem => parseInt(navItem.id) === item.id);

      if (nav) {
        arr.push(nav);
      }
    });

    setFilteredCategories(arr);
  };

  useEffect(() => {
    if (navigationItems.length) {
      getFilteredItems(filteredNav);
    }
  }, [navigationItems, filteredNav]);

  const getCategories = async domain => {
    const response = await window
      .fetch(`${domain[0]}/api/decode/category/count.json`)
      .then(res => res.json())
      .then(data => {
        const filtered = data.data.filter(item => item.count > 0);
        setFilteredNav(filtered);
      });
    return response;
  };

  /*
   * As the browser changes zoom, the visualViewport.width will be different compared to the original window.outerWidth.
   * Check if the current viewport width is <= 70% of window.outerWidth then add modifier class to the Navigation component.
   * 70% reduced of visualViewport width is roughly >= 150% zoom (depends on the screen maybe).
   */
  function handleVisualViewportResize() {
    const widthThreshold = Math.ceil(window.outerWidth * 0.7);
    const { width: visualViewportWidth } = window.visualViewport;

    setIsNavZoomed(Math.ceil(visualViewportWidth) <= Math.ceil(widthThreshold));
  }

  function redirectWithFilter() {
    if (redirectToHome) {
      dispatch({
        type: 'SET_CATEGORY_FILTER',
        payload: redirectSlug.length ? redirectSlug : ''
      });

      history.push({
        pathname: '/decode',
        search: redirectSlug.length ? `category=${redirectSlug}` : ''
      });
    }
  }

  function handleNavButtonClick(redirectSlugString = '') {
    setRedirectSlug(redirectSlugString);

    dispatch({
      type: SET_PREVIOUS_ARTICLE_FOCUS,
      payload: ''
    });

    if (isMenuOpen) {
      setRedirectToHome(true);
      setIsMenuOpen(false);
    } else {
      dispatch({
        type: 'SET_CATEGORY_FILTER',
        payload: ''
      });

      history.push('/decode');
    }
  }

  return (
    logoData !== null && (
      <div className={`${navigationClasses} ${withDelay ? 'navigation--anim-delay' : ''}`}>
        <div className={`navigation__bar ${isNavBarItemsHidden ? 'navigation__bar--hidden' : ''}`}>
          {logoData.length ? (
            <button
              title="Decode by Code &amp; Theory"
              type="button"
              className="navigation__brand"
              onClick={() => {
                handleNavButtonClick();
              }}>
              <DecodeLogo url={logoData[0].url} />
            </button>
          ) : null}
          {isMobileOrTablet ? (
            <div
              style={{
                opacity: isMenuOpen ? 0 : 1,
                transitionProperty: 'opacity',
                transitionDuration: '0.2s',
                transitionTimingFunction: 'cubic - bezier(0.455, 0.03, 0.515, 0.955)',
                transitionDelay: isMenuOpen ? '0s' : '0.4s'
              }}>
              <div
                className="navigation__description"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          ) : (
            <div
              className="navigation__description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}

          <button
            aria-label={`${isMenuOpen ? 'Close Menu' : 'Hamburger Menu'}`}
            type="button"
            className={toggleButtonClasses}
            onClick={() => {
              setRedirectToHome(false);
              setIsNavBarItemsHidden(false);
              setIsMenuOpen(!isMenuOpen);
            }}
          />
          {isArticlePage && navPagination !== null && (
            <NavPagination page={navPagination.page} total={navPagination.total} />
          )}
        </div>
        <CSSTransition
          in={isMenuOpen}
          timeout={1500}
          classNames="navigation__contents"
          onExit={() => redirectWithFilter()}
          unmountOnExit>
          <div className="navigation__contents">
            <div className="navigation__contents-wrapper">
              <a
                href="https://www.codeandtheory.com/"
                className="navigation__candt-logo"
                title="Code &amp; Theory"
                target="_blank"
                rel="noopener noreferrer">
                <Logo fill={logoFill} />
              </a>
              <div
                className="navigation__expanded-copy"
                dangerouslySetInnerHTML={{ __html: expandedCopy }}
              />
              <nav aria-label="Homepage Poster Categories" className="navigation__categories">
                <ul>
                  <li>
                    <button
                      type="button"
                      className={`navigation__category-link ${
                        categoryText === '' ? 'navigation__category-link--active' : ''
                      } ${isBrowserSafari}`}
                      onClick={() => {
                        handleNavButtonClick();
                      }}>
                      Latest
                    </button>
                  </li>
                  {filteredCategories.length
                    ? filteredCategories.map((navItem, index) =>
                        navItem ? (
                          <li key={`nav-item-${index}`} data-id={navItem.id}>
                            <button
                              type="button"
                              className={`navigation__category-link ${
                                categoryText === navItem.slug
                                  ? 'navigation__category-link--active'
                                  : ''
                              } ${isBrowserSafari}`}
                              onClick={() => {
                                handleNavButtonClick(navItem.slug);
                              }}>
                              {navItem.title}
                            </button>
                          </li>
                        ) : null
                      )
                    : null}
                </ul>
              </nav>
            </div>
          </div>
        </CSSTransition>
      </div>
    )
  );
};

export default Navigation;
